import React from 'react';
import { Box, Text } from 'ink';
import { Markdown } from '../../lib/components/Markdown';
import { DEFAULT_TERMINAL_WIDTH } from '../../constants';
import { useTheme } from '../../lib/theme';
import type { ToolState } from './types';

// Columns consumed by BaseTool's chrome (borderLeft + paddingLeft). Children
// that need to size against the terminal width must subtract this. Keep in sync
// with the borderLeft + paddingLeft props on the outer Box below.
export const BASE_TOOL_CHROME_WIDTH = 2;

// `Math.max(1, …)` guards against terminals narrower than the chrome itself.
export const toolContentWidth = (columns: number): number =>
  Math.max(1, columns - BASE_TOOL_CHROME_WIDTH);

interface BaseToolProps {
  label: string;
  state?: ToolState;
  children?: React.ReactNode;
  expanded: boolean;
  showExpandHint?: boolean;
  columns?: number;
}

const StateIndicator: React.FC<{ state?: ToolState }> = ({ state }) => {
  const { tokens } = useTheme();
  switch (state?.type) {
    case 'loading':
      return <Text>...</Text>;
    case 'error':
      return <Text color={tokens.text.danger}> ✗</Text>;
    case 'approval_request':
      return <Text>?</Text>;
    default:
      return null;
  }
};

export const BaseTool: React.FC<BaseToolProps> = ({
  label,
  state,
  children,
  columns = DEFAULT_TERMINAL_WIDTH,
}) => {
  const { tokens } = useTheme();
  const contentWidth = toolContentWidth(columns);
  return (
    <Box
      flexDirection="column"
      marginY={0}
      borderLeft
      borderRight={false}
      borderTop={false}
      borderBottom={false}
      borderStyle="bold"
      borderColor={tokens.text.success}
      paddingLeft={1}
      width={columns}
    >
      <Box width={contentWidth}>
        <Text dimColor>
          {/*
            Reserve 2 cols inside the label row for the StateIndicator (`...`,
            `?`, ` ✗`) that sits next to the markdown in the same flex row. If
            we let the markdown take the full `contentWidth`, the indicator
            gets pushed off the right edge on narrow terminals.
          */}
          <Markdown markdown={label} availableWidth={Math.max(1, contentWidth - 2)} />
        </Text>
        <StateIndicator state={state} />
      </Box>

      {children}

      {state?.type === 'approval_request' && (
        <Box width={contentWidth}>
          <Markdown markdown={state.content} availableWidth={contentWidth} />
        </Box>
      )}

      {state?.type === 'error' && (
        <Box paddingLeft={2} width={contentWidth}>
          <Text color={tokens.text.danger}>{state.error}</Text>
        </Box>
      )}

      {/* {showExpandHint && !expanded && <Text dimColor>Press Ctrl+O to expand</Text>}
      {showExpandHint && expanded && <Text dimColor>Press Ctrl+O to collapse</Text>} */}
    </Box>
  );
};
